"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LoomI18n = void 0;
const i18next_1 = __importDefault(require("i18next"));
const i18next_icu_1 = __importDefault(require("i18next-icu"));
const i18next_xhr_backend_1 = __importDefault(require("i18next-xhr-backend"));
const strip_json_comments_1 = __importDefault(require("strip-json-comments"));
const ChainedBackend_1 = __importDefault(require("./ChainedBackend"));
const Currency_1 = require("./utils/Currency");
var Currency_2 = require("./utils/Currency");
Object.defineProperty(exports, "Currency", { enumerable: true, get: function () { return Currency_2.Currency; } });
const DEFAULT_LANGUAGE = "en";
const DEFAULT_PSEUDOLANGUAGE = "pseudo";
const DEFAULT_NS = "translation";
const DEFAULT_CURRENCY = Currency_1.Currency.Code.USD;
const DEFAULT_TIMEZONE = undefined;
const MAXIMUM_FRACTION_DIGITS = 4;
class LoomI18n {
    constructor() {
        this.defaultCurrency = DEFAULT_CURRENCY;
        this.defaultTimezone = DEFAULT_TIMEZONE;
        this.realT = (key, args) => {
            return this.i18n.t(key, args);
        };
        this.pseudoT = (key, args) => {
            const s = this.i18n.t(key, args).toLowerCase();
            const len = Math.ceil(s.length * 1.3) - s.length;
            const r = "eé".repeat(len).slice(len);
            return `[[${s} ${r}]]`;
        };
        this.tFn = this.realT;
        this.t = (key, args) => {
            try {
                return this.tFn(key, args);
            }
            catch (error) {
                this.missingKeyCallback && this.missingKeyCallback(key);
                console.error(`Schema mismatch for Message with key ${key}:`, error);
                return `[${key}]`;
            }
        };
        this.isInitialized = false;
        this.i18n = i18next_1.default.createInstance();
        this.i18n.init();
    }
    init(initOptions = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (initOptions.defaultLanguage === DEFAULT_PSEUDOLANGUAGE) {
                initOptions.defaultLanguage = DEFAULT_LANGUAGE;
                this.tFn = this.pseudoT;
            }
            initOptions.defaultCurrency && (this.defaultCurrency = initOptions.defaultCurrency);
            initOptions.defaultTimezone && (this.defaultTimezone = initOptions.defaultTimezone);
            this.i18n = i18next_1.default.createInstance();
            this.icu = new i18next_icu_1.default({
                formats: Object.assign(Object.assign({}, getDateTimeFormats(this.defaultTimezone)), getCurrencyFormats(this.defaultCurrency)),
            });
            this.bundledLocaleData = new Set();
            if (initOptions.localesData) {
                Object.keys(initOptions.localesData).forEach(locale => {
                    this.icu.addLocaleData(initOptions.localesData[locale]);
                    this.bundledLocaleData.add(locale);
                });
            }
            const defaultLanguage = initOptions.defaultLanguage || DEFAULT_LANGUAGE;
            if (!this.bundledLocaleData.has(defaultLanguage)) {
                yield this.loadLocaleData(defaultLanguage);
            }
            this.i18n.use(this.icu);
            const bundledLanguages = Object.keys(initOptions.resources || {});
            const i18nInitOptions = {
                lng: defaultLanguage,
                fallbackLng: [...bundledLanguages, DEFAULT_LANGUAGE],
                defaultNS: DEFAULT_NS,
                debug: false,
                lowerCaseLng: false,
                resources: adaptResources(initOptions.resources),
                saveMissing: true,
                missingKeyHandler: () => undefined,
            };
            if (initOptions.remoteResourcesPatterns) {
                return this.initWithBackend(initOptions, i18nInitOptions)
                    .then(() => {
                    this.isInitialized = true;
                    this.initCallback && this.initCallback(i18nInitOptions.lng);
                })
                    .catch(error => {
                    this.initCallback && this.initCallback(i18nInitOptions.lng, error);
                    throw error;
                });
            }
            else {
                return this.initWithBundle(initOptions, i18nInitOptions)
                    .then(() => {
                    this.isInitialized = true;
                    this.initCallback && this.initCallback(i18nInitOptions.lng);
                })
                    .catch(error => {
                    this.initCallback && this.initCallback(i18nInitOptions.lng, error);
                    throw error;
                });
            }
        });
    }
    initSync(initOptions = {}) {
        this.init(initOptions);
    }
    initWithBackend(initOptions, i18nInitOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            i18nInitOptions.partialBundledLanguages = true;
            const backendChain = new ChainedBackend_1.default(initOptions.remoteResourcesPatterns.map(remoteResourcesPattern => {
                return new i18next_xhr_backend_1.default(null, {
                    loadPath: remoteResourcesPattern,
                    parse: (data) => JSON.parse(strip_json_comments_1.default(data)),
                });
            }));
            this.i18n.use(backendChain);
            return new Promise((resolve, reject) => {
                this.i18n.init(i18nInitOptions, (err) => {
                    if (err && !initOptions.resources) {
                        reject(err);
                    }
                    resolve();
                });
            });
        });
    }
    initWithBundle(initOptions, i18nInitOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            i18nInitOptions.initImmediate = false;
            let res;
            let rej;
            const p = new Promise((resolve, reject) => {
                res = resolve;
                rej = reject;
            });
            this.i18n.init(i18nInitOptions, (err) => {
                if (err && (!initOptions.resources || !i18nInitOptions.lng || !initOptions.resources[i18nInitOptions.lng])) {
                    rej(err);
                }
                res();
            });
            return p;
        });
    }
    loadLocaleData(language) {
        const languageCode = language.split("-")[0];
        const localeDataPromise = Promise.resolve();
        if (window && window.document && window.document.createElement) {
            localeDataPromise.then(() => new Promise(resolve => {
                if (this.bundledLocaleData.has(languageCode)) {
                    return resolve();
                }
                const reactIntlLocaleData = window.ReactIntlLocaleData;
                if (reactIntlLocaleData && reactIntlLocaleData[languageCode]) {
                    return resolve();
                }
                const id = `loomi18n-locale-data-${language}`;
                if (window.document.getElementById(id)) {
                    return resolve();
                }
                const localeDataScript = window.document.createElement("script");
                localeDataScript.id = id;
                localeDataScript.async = true;
                localeDataScript.src = `https://locales.plugins.oci.oraclecloud.com/locale-data/${languageCode}.js`;
                localeDataScript.onload = () => {
                    const reactIntlLocaleData = window.ReactIntlLocaleData;
                    if (reactIntlLocaleData && reactIntlLocaleData[languageCode]) {
                        this.icu.addLocaleData(window.ReactIntlLocaleData[languageCode]);
                    }
                    else {
                        console.warn(`Loaded locale-data script for languageCode ${languageCode} did not provide corresponding locale-data`);
                    }
                    return resolve();
                };
                localeDataScript.onerror = err => {
                    console.warn(`Error while loading locale-data script for languageCode ${languageCode}:`, err);
                    return resolve();
                };
                window.document.body.appendChild(localeDataScript);
            }));
        }
        return localeDataPromise;
    }
    changeLanguage(language) {
        return __awaiter(this, void 0, void 0, function* () {
            let realLangauge = language;
            if (realLangauge === DEFAULT_PSEUDOLANGUAGE) {
                realLangauge = DEFAULT_LANGUAGE;
                this.tFn = this.pseudoT;
            }
            else {
                this.tFn = this.realT;
            }
            const localeDataPromise = this.loadLocaleData(realLangauge);
            const changeLanguagePromise = new Promise(resolve => {
                this.i18n.changeLanguage(realLangauge, (err) => {
                    if (err) {
                        console.warn("changeLanguage", err);
                    }
                    resolve();
                });
            });
            return Promise.all([localeDataPromise, changeLanguagePromise])
                .then(() => {
                this.changeLanguageCallback && this.changeLanguageCallback(language);
            })
                .catch(([localeDataError, changeLanguageError]) => {
                if (localeDataError) {
                    console.error("Could not load required locale-data", localeDataError);
                }
                if (changeLanguageError) {
                    console.error("Error while trying to change language", changeLanguageError);
                }
                this.changeLanguageCallback &&
                    this.changeLanguageCallback(realLangauge, localeDataError || changeLanguageError);
                throw localeDataError || changeLanguageError;
            });
        });
    }
    onLanguageChanged(callback) {
        this.changeLanguageCallback = callback;
    }
    onMissingKey(callback) {
        this.missingKeyCallback = callback;
        this.i18n.on("missingKey", (_, __, key) => {
            callback(key);
        });
    }
    onInit(callback) {
        this.initCallback = callback;
    }
    formatCurrency(value, codeOrOptions, displayFormat) {
        let code;
        let format;
        let precision;
        if (codeOrOptions === undefined || typeof codeOrOptions === "string") {
            code = codeOrOptions;
            format = displayFormat;
        }
        if (typeof codeOrOptions === "object") {
            code = codeOrOptions.currencyCode;
            format = codeOrOptions.displayFormat;
            precision = codeOrOptions.precision;
        }
        const options = {};
        options.style = "currency";
        options.currency = code || this.defaultCurrency;
        options.currencyDisplay = format;
        if (precision) {
            options.maximumFractionDigits = MAXIMUM_FRACTION_DIGITS;
        }
        return new Intl.NumberFormat(this.i18n.language, options).format(value);
    }
}
exports.LoomI18n = LoomI18n;
function adaptResources(resources) {
    const i18nextResource = {};
    if (resources) {
        Object.keys(resources).forEach(key => {
            i18nextResource[key] = {
                [DEFAULT_NS]: resources[key],
            };
        });
    }
    return i18nextResource;
}
function getCurrencyFormats(defaultCurrency) {
    const currencyCodes = Object.keys(Currency_1.Currency.Code);
    const currencySymbols = currencyCodes
        .map(v => {
        return { style: "currency", currency: v };
    })
        .reduce((obj, item) => {
        obj[item.currency] = item;
        return obj;
    }, {});
    const currencyNames = currencyCodes
        .map(v => {
        return { style: "currency", currency: v, currencyDisplay: "name" };
    })
        .reduce((obj, item) => {
        obj[`${item.currency}_name`] = item;
        return obj;
    }, {});
    return {
        number: Object.assign(Object.assign({ currency: {
                style: "currency",
                currency: defaultCurrency,
            }, currency_name: {
                style: "currency",
                currency: defaultCurrency,
                currencyDisplay: "name",
            } }, currencySymbols), currencyNames),
    };
}
function getDateTimeFormats(defaultTimezone) {
    const utcTimeZone = { hourCycle: "h23", timeZone: "UTC" };
    const userTimeZone = { timeZone: defaultTimezone };
    const shortDate = { month: "numeric", day: "numeric", year: "numeric" };
    const mediumDate = { month: "short", day: "numeric", year: "numeric" };
    const fullDate = { weekday: "short", month: "short", day: "numeric", year: "numeric" };
    const shortTime = { hour: "numeric", minute: "numeric", timeZoneName: "short" };
    const fullTime = { hour: "numeric", minute: "numeric", second: "numeric", timeZoneName: "short" };
    return {
        time: {
            short: Object.assign(Object.assign({}, shortTime), utcTimeZone),
            short_user: Object.assign(Object.assign({}, shortTime), userTimeZone),
            medium: Object.assign(Object.assign({}, shortTime), utcTimeZone),
            medium_user: Object.assign(Object.assign({}, shortTime), userTimeZone),
            long: Object.assign(Object.assign({}, fullTime), utcTimeZone),
            long_user: Object.assign(Object.assign({}, fullTime), userTimeZone),
            full: Object.assign(Object.assign({}, fullTime), utcTimeZone),
            full_user: Object.assign(Object.assign({}, fullTime), userTimeZone),
        },
        date: {
            short: Object.assign(Object.assign({}, shortDate), utcTimeZone),
            short_user: Object.assign(Object.assign({}, shortDate), userTimeZone),
            medium: Object.assign(Object.assign({}, mediumDate), utcTimeZone),
            medium_user: Object.assign(Object.assign({}, mediumDate), userTimeZone),
            long: Object.assign(Object.assign({}, mediumDate), utcTimeZone),
            long_user: Object.assign(Object.assign({}, mediumDate), userTimeZone),
            full: Object.assign(Object.assign({}, fullDate), utcTimeZone),
            full_user: Object.assign(Object.assign({}, fullDate), userTimeZone),
            fullDateTime: Object.assign(Object.assign(Object.assign({}, fullDate), fullTime), utcTimeZone),
            fullDateTime_user: Object.assign(Object.assign(Object.assign({}, fullDate), fullTime), userTimeZone),
            longDateTime: Object.assign(Object.assign(Object.assign({}, mediumDate), fullTime), utcTimeZone),
            longDateTime_user: Object.assign(Object.assign(Object.assign({}, mediumDate), fullTime), userTimeZone),
            mediumDateTime: Object.assign(Object.assign(Object.assign({}, mediumDate), shortTime), utcTimeZone),
            mediumDateTime_user: Object.assign(Object.assign(Object.assign({}, mediumDate), shortTime), userTimeZone),
            shortDateTime: Object.assign(Object.assign(Object.assign({}, shortDate), shortTime), utcTimeZone),
            shortDateTime_user: Object.assign(Object.assign(Object.assign({}, shortDate), shortTime), userTimeZone),
        },
    };
}
//# sourceMappingURL=LoomI18n.js.map